import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Divider,
  Group,
  Paper,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Switch,
  Text,
  Title,
  UnstyledButton,
} from '@mantine/core'
import { DatePickerInput } from '@mantine/dates'
import { showNotification } from '@mantine/notifications'
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { Appointment, Patient, Slot } from '@medplum/fhirtypes'
import { normalizeErrorString } from '@medplum/core'
import {
  IconAlertCircle,
  IconArrowLeft,
  IconArrowRight,
  IconArrowsExchange2,
  IconCalendar,
  IconCalendarOff,
  IconCheck,
  IconX,
} from '@tabler/icons-react'
import { useMedplum } from '@medplum/react'
import { useClient, type OrpcClient } from '@/hooks/useClient'

type Props = {
  appointment: Appointment
  patient: Patient
  callerOrgId: string
  initialTargetOrgId?: string
  onClose: () => void
  onTransferred: (result: {
    sourceAppointmentId: string
    targetOrgId: string
    patientId: string
  }) => void
}

type ConnectListItem = Awaited<ReturnType<OrpcClient['listOrganizationConnects']>>['items'][number]

/**
 * A `Date`'s LOCAL calendar day as 'YYYY-MM-DD'. Built from local getters (not `toISOString()`, which
 * is UTC) so the value never shifts a day in west-of-UTC zones — see PD-1042.
 */
const toLocalDayString = (date: Date): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const formatTimeRange = (slot: Slot): string => {
  if (!slot.start || !slot.end) return '—'
  const start = new Date(slot.start)
  const end = new Date(slot.end)
  const fmt = (d: Date) => d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
  return `${fmt(start)} – ${fmt(end)}`
}

const formatStartTime = (slot: Slot): string => {
  if (!slot.start) return '—'
  return new Date(slot.start).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  })
}

const formatSlotDuration = (slot: Slot): string => {
  if (!slot.start || !slot.end) return ''
  const ms = new Date(slot.end).getTime() - new Date(slot.start).getTime()
  if (!Number.isFinite(ms) || ms <= 0) return ''
  const minutes = Math.round(ms / 60000)
  if (minutes < 60) return `${minutes} min`
  const hours = Math.floor(minutes / 60)
  const rem = minutes % 60
  return rem === 0 ? `${hours} h` : `${hours} h ${rem} min`
}

function StepBadge({ number }: { number: number }) {
  return (
    <Box
      className="flex items-center justify-center"
      style={{
        width: 24,
        height: 24,
        borderRadius: '50%',
        backgroundColor: 'var(--mantine-color-blue-6)',
        color: 'white',
        fontWeight: 600,
        fontSize: 13,
        flexShrink: 0,
      }}
    >
      {number}
    </Box>
  )
}

function SlotCard({
  slot,
  selected,
  onClick,
}: {
  slot: Slot
  selected: boolean
  onClick: () => void
}) {
  return (
    <UnstyledButton
      onClick={onClick}
      style={{
        position: 'relative',
        padding: '12px 14px',
        borderRadius: 10,
        border: selected
          ? '2px solid var(--mantine-color-blue-6)'
          : '1px solid var(--mantine-color-gray-3)',
        backgroundColor: selected ? 'var(--mantine-color-blue-0)' : 'white',
        transition: 'all 120ms ease',
        textAlign: 'left',
      }}
      className="bg-green-50! hover:shadow-sm"
    >
      <Stack gap={2}>
        <Text fw={600} size="lg" lh={1}>
          {formatStartTime(slot)}
        </Text>
        <Text size="xs" c="dimmed">
          {formatSlotDuration(slot)}
        </Text>
      </Stack>
      {selected && (
        <Box
          style={{
            position: 'absolute',
            top: 6,
            right: 6,
            width: 18,
            height: 18,
            borderRadius: '50%',
            backgroundColor: 'var(--mantine-color-blue-6)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <IconCheck size={12} stroke={3} />
        </Box>
      )}
    </UnstyledButton>
  )
}

export function TransferAppointmentForm({
  appointment,
  patient,
  callerOrgId,
  initialTargetOrgId,
  onClose,
  onTransferred,
}: Props) {
  const client = useClient()
  const medplum = useMedplum()
  const [connects, setConnects] = useState<ConnectListItem[]>([])
  const [isLoadingConnects, setIsLoadingConnects] = useState(false)
  const [targetOrgId, setTargetOrgId] = useState<string | null>(initialTargetOrgId ?? null)
  // Local calendar day as 'YYYY-MM-DD' — Mantine v8 DatePickerInput's native value type. Keeping it a
  // string (never a Date) avoids the UTC round-trip that shifted the field a day back (PD-1042).
  const [selectedDay, setSelectedDay] = useState<string | null>(
    toLocalDayString(appointment.start ? new Date(appointment.start) : new Date()),
  )
  const [slots, setSlots] = useState<Slot[]>([])
  const [isLoadingSlots, setIsLoadingSlots] = useState(false)
  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [preventFutureBookings, setPreventFutureBookings] = useState(true)
  const [callerOrgName, setCallerOrgName] = useState<string>('your organization')

  useEffect(() => {
    if (!callerOrgId) return
    let active = true
    medplum
      .readResource('Organization', callerOrgId)
      .then((org) => {
        if (active) setCallerOrgName(org.name?.trim() || 'your organization')
      })
      .catch(() => {})
    return () => {
      active = false
    }
  }, [medplum, callerOrgId])

  const acceptedConnects = useMemo(
    () =>
      connects.filter((c) => {
        if (c.status !== 'accepted') return false
        const otherId = c.initiator.id === callerOrgId ? c.recipient?.id : c.initiator.id
        return otherId && otherId !== callerOrgId
      }),
    [connects, callerOrgId],
  )

  const orgOptions = useMemo(
    () =>
      acceptedConnects
        .map((c) => {
          const isInitiator = c.initiator.id === callerOrgId
          const counterId = isInitiator ? c.recipient?.id : c.initiator.id
          const counterName = isInitiator ? c.recipient?.name : c.initiator.name
          return counterId
            ? { value: counterId, label: counterName || 'Unknown organization' }
            : null
        })
        .filter((o): o is { value: string; label: string } => o !== null),
    [acceptedConnects, callerOrgId],
  )

  useEffect(() => {
    let active = true
    setIsLoadingConnects(true)
    client
      .listOrganizationConnects({})
      .then((result) => {
        if (active) setConnects(result.items)
      })
      .catch((err) => {
        if (active) {
          showNotification({
            color: 'red',
            title: 'Failed to load connected organizations',
            message: normalizeErrorString(err) || 'Unable to load connected organizations.',
          })
        }
      })
      .finally(() => {
        if (active) setIsLoadingConnects(false)
      })
    return () => {
      active = false
    }
  }, [client])

  const loadSlots = useCallback(async () => {
    if (!targetOrgId || !selectedDay) {
      setSlots([])
      return
    }
    setIsLoadingSlots(true)
    setSelectedSlotId(null)
    try {
      // Send the date-only string; the backend treats it as a calendar day. Do not convert to a
      // full UTC timestamp here — that's what fetched the wrong day in west-of-UTC zones (PD-1042).
      const result = (await client.getSlotsByOrganization({
        organizationId: targetOrgId,
        day: selectedDay,
      })) as unknown as { slots: Slot[] }
      setSlots(result.slots ?? [])
    } catch (err) {
      showNotification({
        color: 'red',
        title: 'Failed to load slots',
        message: normalizeErrorString(err) || 'Unable to load available slots.',
      })
      setSlots([])
    } finally {
      setIsLoadingSlots(false)
    }
  }, [client, targetOrgId, selectedDay])

  useEffect(() => {
    void loadSlots()
  }, [loadSlots])

  const handleSubmit = async () => {
    if (!appointment.id || !targetOrgId) return
    const selectedSlot = slots.find((s) => s.id === selectedSlotId)
    if (!selectedSlot) return

    setIsSubmitting(true)
    try {
      const result = await client.transferAppointment({
        id: appointment.id,
        targetSlot: selectedSlot as unknown as Record<string, never>,
        preventFutureBookings,
      })

      if (!result.success) {
        showNotification({
          color: 'red',
          title: 'Transfer failed',
          message: result.message || 'Unable to transfer appointment.',
          autoClose: false,
        })
        return
      }

      showNotification({
        color: 'green',
        title: 'Appointment transferred',
        message: 'New appointment created in target organization.',
      })
      onTransferred({
        sourceAppointmentId: appointment.id,
        targetOrgId,
        patientId: patient.id ?? '',
      })
    } catch (err) {
      showNotification({
        color: 'red',
        title: 'Transfer failed',
        message: normalizeErrorString(err) || 'Unable to transfer appointment.',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const selectedSlotForLabel = selectedSlotId
    ? slots.find((s) => s.id === selectedSlotId)
    : undefined
  const selectedOrgLabel = targetOrgId
    ? orgOptions.find((o) => o.value === targetOrgId)?.label
    : undefined
  const submitLabel = selectedOrgLabel ? `Transfer to ${selectedOrgLabel}` : 'Transfer appointment'

  return (
    <Paper shadow="sm" radius="lg" p="lg" className="mt-2 border-t-4 border-blue-500 bg-[#f5f7fa]!">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Group gap="sm" wrap="nowrap" align="center">
            <Box
              style={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                backgroundColor: 'var(--mantine-color-blue-1)',
                color: 'var(--mantine-color-blue-7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <IconArrowsExchange2 size={22} stroke={1.75} />
            </Box>
            <Stack gap={2}>
              <Title order={4} fw={600} lh={1.2}>
                Transfer appointment
              </Title>
              <Text size="sm" c="dimmed">
                Move this appointment to a partner organization
              </Text>
            </Stack>
          </Group>
          <ActionIcon
            variant="subtle"
            color="gray"
            onClick={onClose}
            disabled={isSubmitting}
            aria-label="Close transfer form"
          >
            <IconX size={18} />
          </ActionIcon>
        </Group>

        <Divider />

        {/* Step 1: target organization */}
        <Stack gap="xs">
          <Group gap="sm" align="center">
            <StepBadge number={1} />
            <Text fw={600}>Choose organization</Text>
          </Group>
          {!isLoadingConnects && orgOptions.length === 0 ? (
            <Alert icon={<IconAlertCircle size={16} />} color="yellow" variant="light" radius="md">
              No accepted organization connections yet. Set one up under Settings → Organization
              Connects.
            </Alert>
          ) : (
            <Select
              // This form is hosted in a modal above the schedule's hover popover
              // (z-index 400); the dropdown must clear that modal to be visible.
              comboboxProps={{ withinPortal: true, zIndex: 500 }}
              placeholder={isLoadingConnects ? 'Loading organizations…' : 'Select organization'}
              data={orgOptions}
              value={targetOrgId}
              onChange={setTargetOrgId}
              disabled={isLoadingConnects}
              searchable
              radius="md"
              size="md"
              className="max-w-125"
            />
          )}
        </Stack>

        {/* Step 2: pick a day */}
        <Stack gap="xs">
          <Group gap="sm" align="center">
            <StepBadge number={2} />
            <Text fw={600} c={!targetOrgId ? 'dimmed' : undefined}>
              Pick a day
            </Text>
          </Group>
          <DatePickerInput
            popoverProps={{ withinPortal: true, zIndex: 500 }}
            leftSection={<IconCalendar size={16} />}
            value={selectedDay}
            onChange={setSelectedDay}
            className="max-w-125"
            minDate={toLocalDayString(new Date())}
            disabled={!targetOrgId}
            radius="md"
            size="md"
          />
        </Stack>

        {/* Step 3: choose a time slot */}
        <Stack gap="xs">
          <Group gap="sm" align="center" justify="space-between">
            <Group gap="sm" align="center">
              <StepBadge number={3} />
              <Text fw={600} c={!targetOrgId ? 'dimmed' : undefined}>
                Choose a time slot
              </Text>
            </Group>
            {targetOrgId && !isLoadingSlots && slots.length > 0 && (
              <Badge variant="light" color="blue" radius="sm">
                {slots.length} available
              </Badge>
            )}
          </Group>

          {!targetOrgId ? (
            <Box
              style={{
                padding: '24px 16px',
                borderRadius: 10,
                border: '1px dashed var(--mantine-color-gray-3)',
                textAlign: 'center',
              }}
            >
              <Text size="sm" c="dimmed">
                Choose an organization first to see available slots.
              </Text>
            </Box>
          ) : isLoadingSlots ? (
            <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} height={64} radius="md" />
              ))}
            </SimpleGrid>
          ) : slots.length === 0 ? (
            <Box
              style={{
                padding: '32px 16px',
                borderRadius: 10,
                border: '1px dashed var(--mantine-color-gray-3)',
                textAlign: 'center',
              }}
            >
              <Stack gap={6} align="center">
                <IconCalendarOff size={32} color="var(--mantine-color-gray-5)" />
                <Text fw={500}>No available slots on this day</Text>
                <Text size="xs" c="dimmed">
                  Try picking a different day above.
                </Text>
              </Stack>
            </Box>
          ) : (
            <SimpleGrid cols={{ base: 2, sm: 5 }} spacing="sm">
              {slots.map((s) => (
                <SlotCard
                  key={s.id}
                  slot={s}
                  selected={selectedSlotId === s.id}
                  onClick={() => setSelectedSlotId(s.id ?? null)}
                />
              ))}
            </SimpleGrid>
          )}

          {selectedSlotForLabel && (
            <Text size="xs" c="dimmed">
              Selected: <strong>{formatTimeRange(selectedSlotForLabel)}</strong>
            </Text>
          )}
        </Stack>

        <Divider />

        <Switch
          checked={preventFutureBookings}
          onChange={(e) => setPreventFutureBookings(e.currentTarget.checked)}
          disabled={isSubmitting}
          label={
            <span>
              Prevent future bookings at <strong>{callerOrgName}</strong>
            </span>
          }
          description="The patient won't see this organization's slots when booking on their own. You can undo by transferring again with this switch off."
        />

        <Divider />

        {/* Footer */}
        <Group justify="flex-end" gap="sm">
          <Button
            variant="default"
            leftSection={<IconArrowLeft size={16} />}
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!targetOrgId || !selectedSlotId || isSubmitting}
            loading={isSubmitting}
            rightSection={<IconArrowRight size={16} />}
          >
            {submitLabel}
          </Button>
        </Group>
      </Stack>
    </Paper>
  )
}
